import { ApolloError } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { mergeFilteredContents } from '../../apollo/merge';
import {
  ContentFilter,
  useFilterContentsLazyQuery,
  useTotalContentCountLazyQuery,
} from '../../graphql/types';
import { useContentLibraryFilterContext } from './FilterContext';
import { getComponentFromFormat } from './util';

const CONTENT_PAGE_SIZE = 10;

export const useContentsQuery = ({ isOpen }: { isOpen: boolean }) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [fetchContents, { data, loading, error, fetchMore }] =
    useFilterContentsLazyQuery();
  const [fetchContentTotalCount, { data: totalContentsCount }] =
    useTotalContentCountLazyQuery();
  const [fetchMoreError, setFetchMoreError] = useState<ApolloError | undefined>(
    undefined,
  );

  const { appliedFilters, search } = useContentLibraryFilterContext();

  const totalCount = totalContentsCount?.totalContents?.count;
  const filteredCount = data?.contents?.count;
  const hasNextPage = data?.contents?.pageInfo?.hasNextPage;
  const endCursor = data?.contents?.pageInfo?.endCursor;

  const filters = useMemo(() => {
    const contentFilter: ContentFilter = {};

    if (appliedFilters.formats.length > 0) {
      contentFilter.component = getComponentFromFormat(
        appliedFilters.formats.map((filter) => filter.queryValue),
      );
    }

    if (appliedFilters.genres.length > 0) {
      contentFilter.genre = appliedFilters.genres.map(
        (filter) => filter.queryValue,
      );
    }

    if (appliedFilters.gameplanPrimaryComponents.length > 0) {
      contentFilter.gameplanPrimaryComponent =
        appliedFilters.gameplanPrimaryComponents.map(
          (filter) => filter.queryValue,
        );
    }

    if (appliedFilters.gameplanPrimarySubcomponents.length > 0) {
      contentFilter.gameplanPrimarySubcomponents =
        appliedFilters.gameplanPrimarySubcomponents.map(
          (filter) => filter.queryValue,
        );
    }

    if (appliedFilters.gameplanSecondaryComponents.length > 0) {
      contentFilter.gameplanSecondaryComponent =
        appliedFilters.gameplanSecondaryComponents.map(
          (filter) => filter.queryValue,
        );
    }

    if (appliedFilters.gameplanSecondarySubcomponents.length > 0) {
      contentFilter.gameplanSecondarySubcomponents =
        appliedFilters.gameplanSecondarySubcomponents.map(
          (filter) => filter.queryValue,
        );
    }

    if (appliedFilters.associatedProject.length > 0) {
      contentFilter.associatedProject = appliedFilters.associatedProject.map(
        (filter) => filter.queryValue,
      );
    }

    if (appliedFilters.intensities.length > 0) {
      contentFilter.intensity = appliedFilters.intensities.map(
        (filter) => filter.queryValue,
      );
    }

    if (appliedFilters.levels.length > 0) {
      contentFilter.level = appliedFilters.levels.map(
        (filter) => filter.queryValue,
      );
    }

    if (appliedFilters.splits.length > 0) {
      contentFilter.split = appliedFilters.splits.map(
        (filter) => filter.queryValue,
      );
    }

    if (appliedFilters.duration) {
      contentFilter.duration = appliedFilters.duration.queryValue;
    }

    if (appliedFilters.performanceQuality) {
      contentFilter.performanceQuality = [
        appliedFilters.performanceQuality.queryValue,
      ];
    }

    if (appliedFilters.isExperiential) {
      contentFilter.isExperiential = appliedFilters.isExperiential.queryValue;
    }

    if (search.title) {
      contentFilter.search = search.title;
    }
    return contentFilter;
  }, [appliedFilters, search.title]);

  useEffect(() => {
    if (isOpen) {
      fetchContents({
        variables: {
          filter: filters,
          input: {
            first: CONTENT_PAGE_SIZE,
          },
          movementProgressionsInput: {
            first: 3,
          },
          movementProgressionsFilter: {
            search: filters.search,
          },
        },
      });
    }
  }, [isOpen, filters, fetchContents]);

  useEffect(() => {
    if (isOpen) {
      fetchContentTotalCount();
    }
  }, [isOpen, fetchContentTotalCount]);

  const loadMore = async () => {
    if (!loading && !isFetchingMore && hasNextPage) {
      setIsFetchingMore(true);
      await fetchMore({
        variables: {
          filter: filters,
          input: {
            first: CONTENT_PAGE_SIZE,
            after: endCursor,
          },
          movementProgressionsInput: {
            first: 3,
          },
          movementProgressionsFilter: {
            search: filters.search,
          },
        },
        updateQuery: mergeFilteredContents,
      }).catch((e: ApolloError) => {
        setFetchMoreError(e);
      });

      setIsFetchingMore(false);
    }
  };

  return {
    fetch,
    loadMore,
    loading,
    error,
    isFetchingMore,
    fetchMoreError,
    setFetchMoreError,
    totalCount,
    filteredCount,
    data,
    hasNextPage,
  };
};
